import { supabase } from './supabase';
import type { OrgSettings } from './pos-types';

export interface MergeOrgSettingsOptions {
  /**
   * Keys to UNSET. The server-side merge never drops a key by omission, so an
   * intentional unset must be explicit (e.g. clearing receipt_greeting).
   */
  remove?: string[];
  /**
   * Optional optimistic-concurrency baseline (organizations.updated_at). When
   * passed and the row has since changed, the RPC raises 'settings_conflict'.
   * Omit for ordinary owner self-service — the live-merge already prevents key loss.
   */
  expectedUpdatedAt?: string | null;
}

/**
 * Write-safe settings update (FINDINGS_JSONB_RISK #1). Routes through the
 * `update_org_settings` RPC, which live-merges `settings || patch - remove` under
 * the row lock, so a stale/partial write can only SET the keys it carries — it can
 * never drop the rest. Returns the merged settings (truth) so callers need not
 * re-send a whole object. Replaces every whole-column `.update({ settings })` write.
 */
export async function mergeOrgSettings(
  orgId: string,
  patch: Record<string, unknown>,
  opts: MergeOrgSettingsOptions = {},
): Promise<OrgSettings> {
  const { data, error } = await supabase.rpc('update_org_settings', {
    p_org_id: orgId,
    p_patch: patch,
    p_remove: opts.remove ?? [],
    p_expected_updated_at: opts.expectedUpdatedAt ?? null,
  });
  if (error) throw error;
  return data as OrgSettings;
}

/**
 * SETTINGS-SAVE-01 — write ONE settings leaf (e.g. ['command_types','delivery']) via the
 * `update_org_setting` RPC: a deep `jsonb_set` on the CURRENT row (never a shallow `||`,
 * never a cached snapshot), so it can only set the one key it carries and can never revert
 * siblings. Authorized for org_owner / manager / super_admin (the RPC enforces it). Returns
 * the merged settings (server truth). Toggles/fields call this for their own key only.
 */
export async function updateOrgSettingPath(
  orgId: string,
  path: string[],
  value: unknown,
): Promise<OrgSettings> {
  const { data, error } = await supabase.rpc('update_org_setting', {
    p_org_id: orgId,
    p_path: path,
    p_value: value as never,
  });
  if (error) throw error;
  return data as OrgSettings;
}

/**
 * SETTINGS-SAVE-01 — write ONE editable organizations COLUMN (`name`,
 * `business_day_reset_hour`) via `update_org_field`: same owner/manager/super authz, per field,
 * no whole-object `.update()` from cached state. business_day_reset_hour moves the accounting day.
 */
export async function updateOrgFieldValue(
  orgId: string,
  field: 'name' | 'business_day_reset_hour',
  value: unknown,
): Promise<void> {
  const { error } = await supabase.rpc('update_org_field', {
    p_org_id: orgId,
    p_field: field,
    p_value: value as never,
  });
  if (error) throw error;
}
